// A finding's status and the actions on it -- ONE component for the Health and Compliance
// tabs (consultant page and customer portal alike) and the consultant customer page.
//
// Findings status v2 (services/finding_status.py on the server):
//   statuses   open -> acknowledged -> fix_submitted -> resolved (only a scan resolves)
//              dismissed (staff, with a reason)
//   "Couldn't check" is NOT a status: it is the finding's last check outcome
//   (last_check_outcome = 'not_evaluated'), shown next to the status.
// Who may do what comes from the signed-in user's permissions (/api/auth/me), never from
// the role name alone: respond_to_findings (customer, consultant, admin) acknowledges and
// submits fixes; a customer asks for a dismissal; edit_findings (staff) dismisses,
// declines a request and reopens. The server enforces the same table -- this only hides
// buttons that would be refused.
// An admin's portal preview (<CustomerView>) is READ-ONLY (Scott, 2026-10-06): the server
// refuses every action there (security/auth.py PREVIEW_READ_ONLY), so the preview shows
// a note in place of the customer's buttons.
import { useState } from 'react'
import { useAuth } from '../../AuthProvider'
import {
  acknowledgeFinding, submitFixForFinding, requestFindingDismissal,
  declineFindingDismissal, dismissFinding, reopenFinding,
} from './api'
import { ACTIVE_STATUSES, STATUS_LABEL, statusOf } from './findings'
import './FindingStatus.css'

const fmtDate = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString()
}

// What the viewer may do. Mirrors the server's WHO_MAY / TRANSITIONS.
function useFindingPermissions() {
  const { profile, customerView } = useAuth()
  const perms = new Set(profile?.permissions || [])
  const staff = perms.has('edit_findings')
  return {
    staff,
    respond: staff || perms.has('respond_to_findings'),
    customer: profile?.role === 'customer',
    preview: !!customerView,
  }
}

const ACTIONS = {
  acknowledge:     { label: 'Acknowledge', call: acknowledgeFinding, needsReason: false, cls: 'ack' },
  submit_fix:      { label: 'Fix submitted', call: submitFixForFinding, needsReason: false, cls: 'resolve',
                     prompt: 'What was fixed? (optional)' },
  request_dismiss: { label: 'Request dismissal', call: requestFindingDismissal, needsReason: true, cls: 'dismiss',
                     prompt: 'Why should this finding be dismissed?' },
  dismiss:         { label: 'Dismiss', call: dismissFinding, needsReason: true, cls: 'dismiss',
                     prompt: 'Why is this finding being dismissed?' },
  decline:         { label: 'Decline request', call: declineFindingDismissal, needsReason: true, cls: 'dismiss',
                     prompt: 'Why is the dismissal request declined?' },
  reopen:          { label: 'Reopen', call: reopenFinding, needsReason: true, cls: 'ack',
                     prompt: 'Why is this finding being reopened?' },
}

export function availableActions(finding, { staff, respond, customer }) {
  const s = statusOf(finding)
  const pending = !!finding?.pending_dismissal_request
  const out = []
  if (respond && s === 'open') out.push('acknowledge')
  if (respond && (s === 'open' || s === 'acknowledged')) out.push('submit_fix')
  if (customer && respond && ACTIVE_STATUSES.includes(s) && !pending) out.push('request_dismiss')
  if (staff && (ACTIVE_STATUSES.includes(s) || s === 'resolved')) out.push('dismiss')
  if (staff && pending && ACTIVE_STATUSES.includes(s)) out.push('decline')
  if (staff && ['acknowledged', 'fix_submitted', 'dismissed'].includes(s)) out.push('reopen')
  return out
}

// Status pill + couldn't-check + who/when/why + pending request.
export function FindingStatusLine({ finding }) {
  const s = statusOf(finding)
  const couldNotCheck = finding?.last_check_outcome === 'not_evaluated'
  const unverified = finding?.last_check_outcome === 'unverified'
  // clean because every failing row was outside the check's scope: say what was left out
  const scopedOut = finding?.last_check_outcome === 'clean' && finding?.last_check_kind === 'scoped_out'
    && !!finding?.last_check_reason
  const who = finding?.status_updated_by
  const when = fmtDate(finding?.status_updated_at)
  const pending = finding?.pending_dismissal_request
  return (
    <div className="fs-line" data-testid="finding-status">
      <span className={`fs-pill fs-pill-${s}`}>{STATUS_LABEL[s] || s}</span>
      {couldNotCheck && (
        <span className="fs-pill fs-pill-unchecked" title={finding?.last_check_reason || ''}>
          Couldn't check
        </span>
      )}
      {unverified && <span className="fs-pill fs-pill-unchecked">Not yet verified</span>}
      {s !== 'open' && (who || finding?.status_reason) && (
        <span className="fs-meta">
          {who ? `by ${who}` : ''}{when ? ` · ${when}` : ''}
          {finding?.status_reason ? ` — ${finding.status_reason}` : ''}
        </span>
      )}
      {couldNotCheck && finding?.last_check_reason && (
        <div className="fs-note" data-testid="finding-check-reason">{finding.last_check_reason}</div>
      )}
      {scopedOut && (
        <div className="fs-note" data-testid="finding-scoped-out">
          Last check found nothing in scope: {finding.last_check_reason}
        </div>
      )}
      {pending && (
        <div className="fs-note fs-pending" data-testid="finding-pending-request">
          Dismissal requested{pending.requested_by ? ` by ${pending.requested_by}` : ''}
          {pending.requested_at ? ` · ${fmtDate(pending.requested_at)}` : ''}
          {pending.reason ? ` — ${pending.reason}` : ''}
        </div>
      )}
    </div>
  )
}

// The buttons. `prefix` reuses the tab's own button styles (ht / ct / cp).
// onChange(updated finding) after a successful action.
export function FindingActions({ finding, onChange, prefix = 'ht' }) {
  const perms = useFindingPermissions()
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(null)   // action key awaiting a reason
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  if (!finding?.id) return null
  const actions = availableActions(finding, perms)
  if (!actions.length) return null
  if (perms.preview) {
    return (
      <div className="fs-note fs-preview" data-testid="finding-preview-read-only">
        Preview is read-only: the customer's actions on this finding are not available here.
      </div>
    )
  }

  const run = async (key, why) => {
    const a = ACTIONS[key]
    if (a.needsReason && !why.trim()) { setError('A reason is required.'); return }
    setBusy(true); setError('')
    const r = await a.call(finding.id, why.trim())
    setBusy(false)
    if (!r.ok) { setError(r.error || 'The change could not be saved.'); return }
    setAsking(null); setReason('')
    const next = { ...finding, status: r.data?.status || finding.status }
    if (key === 'request_dismiss') {
      next.pending_dismissal_request = { id: r.data?.request_id, reason: why.trim(), requested_by: 'you',
                                         requested_at: new Date().toISOString() }
    } else {
      if (key === 'dismiss' || key === 'decline') next.pending_dismissal_request = null
      if (r.data?.status && r.data.status !== finding.status) {
        next.status_reason = why.trim()
        next.status_updated_by = 'you'
        next.status_updated_at = new Date().toISOString()
      }
    }
    onChange?.(next)
  }

  const click = (key) => (e) => {
    e.stopPropagation()
    if (busy) return
    if (ACTIONS[key].needsReason || ACTIONS[key].prompt) { setAsking(key); setReason(''); setError(''); return }
    run(key, '')
  }

  return (
    <div className="fs-actions" onClick={e => e.stopPropagation()}>
      <div className={`${prefix}-res-actions`} data-testid="finding-actions">
        {actions.map(key => (
          <button
            key={key}
            className={`${prefix}-res-btn ${prefix}-rb-${ACTIONS[key].cls}`}
            onClick={click(key)}
            disabled={busy}
            data-action={key}
          >
            {ACTIONS[key].label}
          </button>
        ))}
      </div>
      {asking && (
        <div className="fs-reason" data-testid="finding-reason-form">
          <label className="fs-reason-label">{ACTIONS[asking].prompt}</label>
          <textarea
            className="fs-reason-input"
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={2}
            maxLength={1000}
            autoFocus
          />
          <div className="fs-reason-buttons">
            <button className={`${prefix}-res-btn ${prefix}-rb-${ACTIONS[asking].cls}`}
                    onClick={e => { e.stopPropagation(); run(asking, reason) }}
                    disabled={busy || (ACTIONS[asking].needsReason && !reason.trim())}
                    data-testid="finding-reason-submit">
              {busy ? 'Saving…' : ACTIONS[asking].label}
            </button>
            <button className={`${prefix}-res-btn ${prefix}-rb-ack`}
                    onClick={e => { e.stopPropagation(); setAsking(null); setError('') }}
                    disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <div className="fs-error" role="alert">{error}</div>}
    </div>
  )
}
